import { type Lead } from "../../types/lead.types.ts"

interface LeadFinancialSnapshot {
  salesPrice: number | null
  plannedPayable: number | null
  margin: number | null
  marginPercent: number | null
}

const toAmount = (value: number | string | null | undefined): number | null => {
  if (value == null || value === "") return null
  const numeric = typeof value === "string" ? parseFloat(value) : value
  return Number.isFinite(numeric) ? numeric : null
}

interface LeadFinancialMargin {
  margin: number | null
  marginPercent: number | null
}

/**
 * Sales price less planned payable — stays absent while neither figure is
 * present, so an empty card reads as unset rather than as a $0 margin. Shared
 * by the read-only snapshot and the Overview card's live edit-mode preview,
 * so the two never drift apart.
 */
function computeMargin(
  salesPrice: number | null,
  plannedPayable: number | null
): LeadFinancialMargin {
  const margin =
    salesPrice == null && plannedPayable == null
      ? null
      : (salesPrice ?? 0) - (plannedPayable ?? 0)

  const marginPercent =
    margin != null && salesPrice != null && salesPrice !== 0
      ? (margin / salesPrice) * 100
      : null

  return { margin, marginPercent }
}

function leadFinancialSnapshot(lead: Lead | null): LeadFinancialSnapshot {
  const salesPrice = toAmount(lead?.sales_price)
  const plannedPayable = toAmount(lead?.planned_payable)

  return {
    salesPrice,
    plannedPayable,
    ...computeMargin(salesPrice, plannedPayable)
  }
}

export {
  type LeadFinancialSnapshot,
  leadFinancialSnapshot,
  computeMargin,
  toAmount
}
